/**
 * Звуки событий торговли. Файлов нет — короткие мелодии синтезируются
 * WebAudio: заливка звучит вверх, выливка вниз, выливка в плюс — аккордом,
 * новая грамота — фанфарой. По мелодии понятно, что случилось, не глядя.
 *
 * Выключатель — в колокольчике (`sb_trade_sound`). Браузер не даёт звучать
 * вкладке, в которой человек ещё ничего не нажал: до первого клика
 * AudioContext стоит на паузе, и мы просто молчим.
 */
const KEY = 'sb_trade_sound';

export const tradeSoundOn = (): boolean => {
  try { return localStorage.getItem(KEY) !== '0'; } catch { return true; }
};
export const setTradeSound = (on: boolean): void => {
  try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* приватный режим */ }
};

let ctx: AudioContext | null = null;
function audio(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctx = window.AudioContext
        || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    return ctx;
  } catch { return null; }
}

type Note = { f: number; at: number; len: number; vol?: number };

// Тихо и мягко: чистый синус через срез верхов, плавная атака, громкость в
// несколько раз ниже звука алерта. Первая версия (треугольная волна, 0.09)
// резала слух — событие должно быть слышно, но не дёргать.
const VOL = 0.028;

function play(notes: Note[]): void {
  const a = audio();
  if (!a || a.state !== 'running') return;
  const t0 = a.currentTime + 0.02;
  const soft = a.createBiquadFilter();
  soft.type = 'lowpass';
  soft.frequency.value = 1400;
  soft.connect(a.destination);
  for (const n of notes) {
    const osc = a.createOscillator();
    const gain = a.createGain();
    osc.type = 'sine';
    osc.frequency.value = n.f;
    const start = t0 + n.at;
    const peak = n.vol ?? VOL;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + n.len);
    osc.connect(gain).connect(soft);
    osc.start(start);
    osc.stop(start + n.len + 0.05);
  }
}

// Низкий мягкий регистр. Ноты: A3 220, C4 262, E4 330, G4 392, A4 440, C5 523, E5 659.
const MELODY: Record<string, Note[]> = {
  enter:  [{ f: 330, at: 0, len: 0.22 }, { f: 440, at: 0.12, len: 0.34 }],
  exit:   [{ f: 440, at: 0, len: 0.22 }, { f: 330, at: 0.12, len: 0.34 }],
  profit: [{ f: 392, at: 0, len: 0.24 }, { f: 523, at: 0.12, len: 0.24 }, { f: 659, at: 0.24, len: 0.5 }],
  loss:   [{ f: 262, at: 0, len: 0.26 }, { f: 220, at: 0.15, len: 0.42 }],
  award:  [{ f: 392, at: 0, len: 0.2 }, { f: 523, at: 0.13, len: 0.2 }, { f: 659, at: 0.26, len: 0.2 },
           { f: 784, at: 0.39, len: 0.7 }, { f: 523, at: 0.39, len: 0.7, vol: 0.014 }],
};

export type SoundName = 'enter' | 'exit' | 'profit' | 'loss' | 'award';

/** Проиграть звук события. `force` — для кнопки «послушать» в настройках. */
export function playSound(name: SoundName, force = false): void {
  if (!force && !tradeSoundOn()) return;
  play(MELODY[name]);
}
